// src/pages/SubCategoryList.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Snackbar, Alert, TextField,
  Pagination, Stack, Tooltip, Dialog, DialogContent, DialogActions,
  DialogContentText, DialogTitle, MenuItem, CircularProgress, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { getAllSubCategories, deleteSubCategory, getAllCategories } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import SubCategoryFormDialog from './SubCategoryFormDialog';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import SortableTh from './common/SortableTh';
import { useServerSort } from '../hooks/useServerSort';

function SubCategoryList() {
  const [pagina, setPagina] = useState(1);
  const [qtde] = useState(20);
  const [items, setItems] = useState([]);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalItens, setTotalItens] = useState(0);
  const [carregando, setCarregando] = useState(false);

  const [categorias, setCategorias] = useState([]);
  const [categoriaId, setCategoriaId] = useState('');

  const [searchText, setSearchText] = useState('');
  const [termo, setTermo] = useState('');

  const [confirmItem, setConfirmItem] = useState(null);
  const [excluindo, setExcluindo] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  const notificar = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getAllCategories({ indice: 1, qtde: 200 });
        setCategorias(data.items ?? []);
      } catch {
        // filtro opcional: silencioso
      }
    })();
  }, []);

  const { orderBy, order, requestSort: aplicarOrdem } = useServerSort('descricao', 'asc');
  const requestSort = (key) => {
    setPagina(1);
    aplicarOrdem(key);
  };

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await getAllSubCategories({
        indice: pagina, qtde, termo, categoriaId: categoriaId || undefined,
        ordenar: orderBy, direcao: order,
      });
      setItems(data.items ?? []);
      setTotalPaginas(data.totalPages ?? 1);
      setTotalItens(data.totalItems ?? 0);
    } catch (e) {
      notificar(getApiErrorMessage(e, 'Erro ao carregar subcategorias'), 'error');
    } finally {
      setCarregando(false);
    }
  }, [pagina, qtde, termo, categoriaId, orderBy, order]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => { setPagina(1); }, [categoriaId]);

  const sorted = items;

  const onBuscar = () => { setPagina(1); setTermo(searchText.trim()); };
  const onKeyDown = (e) => e.key === 'Enter' && onBuscar();

  const abrirNova = () => { setEditing(null); setFormOpen(true); };
  const abrirEdicao = (subCategoria) => { setEditing(subCategoria); setFormOpen(true); };

  const aoSalvar = (msg) => {
    setFormOpen(false);
    setEditing(null);
    notificar(msg);
    carregar();
  };

  const excluir = async () => {
    setExcluindo(true);
    try {
      await deleteSubCategory(confirmItem.id);
      notificar('Subcategoria excluída');
      setConfirmItem(null);
      if (items.length === 1 && pagina > 1) setPagina(pagina - 1);
      else carregar();
    } catch (e) {
      setConfirmItem(null);
      notificar(getApiErrorMessage(e, 'Não foi possível excluir a subcategoria.'), 'error');
    } finally {
      setExcluindo(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Subcategorias"
        subtitle="Subdivisão das categorias, equivalente ao subgrupo no ZenPDV."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNova}>
            Nova subcategoria
          </Button>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField
            size="small" placeholder="Buscar por descrição..."
            value={searchText} onChange={(e) => setSearchText(e.target.value)} onKeyDown={onKeyDown}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              endAdornment: !!searchText && (
                <IconButton size="small" onClick={() => { setSearchText(''); setTermo(''); setPagina(1); }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )
            }}
            sx={{ flexGrow: 1, minWidth: 240 }}
          />
          <Button variant="outlined" onClick={onBuscar}>Buscar</Button>

          <TextField
            select size="small" label="Categoria" value={categoriaId}
            onChange={(e) => setCategoriaId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value=""><em>Todas</em></MenuItem>
            {categorias.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>{c.descricao}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      <ResponsiveTable>
        <Table>
          <TableHead>
            <TableRow>
              <SortableTh id="id" label="Código" width={100} orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="descricao" label="Descrição" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="categoriaNome" label="Categoria" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right" width={120}><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {carregando ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {termo || categoriaId
                    ? 'Nenhuma subcategoria encontrada com os filtros atuais'
                    : 'Nenhuma subcategoria cadastrada'}
                </TableCell>
              </TableRow>
            ) : sorted.map((s) => (
              <TableRow key={s.id} hover sx={{ cursor: 'pointer' }} onClick={() => abrirEdicao(s)}>
                <TableCell data-label="Código">{s.id}</TableCell>
                <TableCell data-label="Descrição">{s.descricao}</TableCell>
                <TableCell data-label="Categoria">
                  {s.categoriaNome
                    ? <Chip size="small" variant="outlined" label={s.categoriaNome} />
                    : '-'}
                </TableCell>
                <TableCell align="right" data-actions="" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Editar">
                    <IconButton color="primary" onClick={() => abrirEdicao(s)}><EditIcon /></IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton color="error" onClick={() => setConfirmItem(s)}><DeleteIcon /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>

      <Stack spacing={1} sx={{ mt: 2, alignItems: 'center' }}>
        <Pagination count={totalPaginas} page={pagina} onChange={(_e, p) => setPagina(p)} color="primary" />
        <Typography variant="body2" color="text.secondary">
          Exibindo {items.length} de {totalItens} subcategorias (página {pagina}/{totalPaginas})
        </Typography>
      </Stack>

      <SubCategoryFormDialog
        open={formOpen}
        subCategory={editing}
        onClose={() => setFormOpen(false)}
        onSaved={aoSalvar}
      />

      <Dialog open={!!confirmItem} onClose={() => setConfirmItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir subcategoria</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Excluir <strong>{confirmItem?.descricao}</strong>? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmItem(null)} color="inherit" disabled={excluindo}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={excluir} disabled={excluindo}>Excluir</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SubCategoryList;
